import { useState, type KeyboardEvent } from 'react';
import { formatPersianNumber } from '../../../app/components/foundation';

interface TrendPoint { period: string; value: number; users: number; orders: number }
interface StatusPoint { status: string; count: number }

const STATUS_LABELS: Record<string, string> = {
  paid: 'پرداخت شده',
  pending: 'در انتظار',
  failed: 'ناموفق',
  rejected: 'ناموفق',
  processing: 'در حال پردازش',
  deleted: 'حذف شده',
  expired: 'منقضی شده',
  cancelled: 'لغو شده',
};

function getStatusLabel(status: string) {
  const normalizedStatus = status.trim().toLowerCase();
  return STATUS_LABELS[normalizedStatus] || status.trim() || 'نامشخص';
}

function formatCompactPeriod(period: string) {
  const date = new Date(period);
  if (Number.isNaN(date.getTime())) return period;
  return new Intl.DateTimeFormat('fa-IR', { month: 'short', day: 'numeric' }).format(date).replace(/\s+/g, ' ');
}

function statusTone(status: string) {
  const normalized = status.trim().toLowerCase();
  if (['paid', 'active', 'healthy', 'success', 'completed'].includes(normalized)) return 'is-success';
  if (['pending', 'processing', 'warning', 'review'].includes(normalized)) return 'is-warning';
  if (normalized === 'cancelled') return 'is-cancelled';
  if (normalized === 'deleted') return 'is-deleted';
  if (normalized === 'expired') return 'is-expired';
  if (['failed', 'rejected', 'error'].includes(normalized)) return 'is-failed';
  return 'is-neutral';
}

const SERIES = [
  { key: 'revenue', label: 'درآمد', color: 'var(--color-chart-primary)' },
  { key: 'orders', label: 'سفارش‌ها', color: 'var(--color-chart-secondary)' },
  { key: 'users', label: 'رشد کاربران', color: 'var(--vania-accent)' },
] as const;

function pointCoordinates(values: number[], max: number) {
  return values.map((value, index) => {
    const x = values.length < 2 ? 150 : (index / (values.length - 1)) * 300;
    const y = 92 - (value / max) * 76;
    return `${x},${y}`;
  }).join(' ');
}

function handleKeyboardActivation(event: KeyboardEvent<Element>, activate: () => void) {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  activate();
}

export function DashboardTrendChart({ points }: { points: TrendPoint[] }) {
  const [active, setActive] = useState<number | null>(null);
  const visible = points.slice(-8);
  const max = Math.max(1, ...visible.flatMap((item) => [item.value, item.orders, item.users]));
  const series = {
    revenue: visible.map((item) => item.value),
    orders: visible.map((item) => item.orders),
    users: visible.map((item) => item.users),
  };

  return (
    <div className="vania-command-trend-chart" dir="ltr">
      <div className="vania-command-chart-legend" dir="rtl">
        {SERIES.map((item) => <span key={item.key}><i className={`vania-command-series-dot ${item.key}`} />{item.label}</span>)}
      </div>
      <div className="vania-command-chart-plot">
        <svg viewBox="0 0 300 100" role="img" aria-label="روند درآمد، سفارش‌ها و رشد کاربران" preserveAspectRatio="none">
          {[16, 40, 64, 92].map((y) => <line key={y} x1="0" x2="300" y1={y} y2={y} className="vania-command-chart-gridline" />)}
          {SERIES.map((item) => <polyline key={item.key} points={pointCoordinates(series[item.key], max)} className={`vania-command-chart-line ${item.key}`} />)}
          {visible.map((item, index) => <g key={`${item.period}-${index}`} className="vania-command-chart-point-group" role="button" tabIndex={0} aria-label={`جزئیات ${item.period}`} onMouseEnter={() => setActive(index)} onMouseLeave={() => setActive(null)} onFocus={() => setActive(index)} onBlur={() => setActive(null)}>
            <circle cx={visible.length < 2 ? 150 : (index / (visible.length - 1)) * 300} cy={92 - (item.value / max) * 76} r="1.4" className="vania-command-chart-point revenue" />
            <circle cx={visible.length < 2 ? 150 : (index / (visible.length - 1)) * 300} cy={92 - (item.orders / max) * 76} r="1.4" className="vania-command-chart-point orders" />
            <circle cx={visible.length < 2 ? 150 : (index / (visible.length - 1)) * 300} cy={92 - (item.users / max) * 76} r="1.4" className="vania-command-chart-point users" />
          </g>)}
        </svg>
        {active !== null && visible[active] ? <div className="vania-command-chart-tooltip" style={{ left: `${Math.min(84, Math.max(16, (active / Math.max(1, visible.length - 1)) * 100))}%` }} dir="rtl"><strong className="vania-command-chart-tooltip-date">{visible[active].period}</strong><span>درآمد: {formatPersianNumber(visible[active].value)}</span><span>سفارش: {formatPersianNumber(visible[active].orders)}</span><span>کاربر: {formatPersianNumber(visible[active].users)}</span></div> : null}
      </div>
      <div className="vania-command-chart-labels" dir="ltr">{visible.map((item, index) => {
        const middleIndex = Math.floor((visible.length - 1) / 2);
        const hideOnMobile = visible.length > 3 && index !== 0 && index !== middleIndex && index !== visible.length - 1;
        return <span key={item.period} className={hideOnMobile ? 'is-mobile-hidden' : undefined}><span className="vania-command-chart-label-desktop">{formatCompactPeriod(item.period)}</span><span className="vania-command-label-mobile">{formatCompactPeriod(item.period)}</span></span>;
      })}</div>
    </div>
  );
}

export function DashboardStatusChart({ items }: { items: StatusPoint[] | null | undefined; total: number }) {
  const [active, setActive] = useState<number | null>(null);
  const visible = Array.isArray(items)
    ? Array.from(items.reduce((statusMap, item) => {
      const status = item?.status?.trim() || 'نامشخص';
      const count = Number(item?.count);
      if (!Number.isFinite(count) || count <= 0) return statusMap;
      statusMap.set(status, (statusMap.get(status) ?? 0) + count);
      return statusMap;
    }, new Map<string, number>()).entries()).map(([status, count]) => ({ status, count }))
    : [];
  const sum = visible.reduce((value, item) => value + item.count, 0);
  let offset = 0;

  return <div className="vania-command-status-chart" dir="rtl">
    <div className="vania-command-status-visual">
      <svg viewBox="0 0 120 120" role="img" aria-label="توزیع وضعیت سفارش‌ها">
        <circle cx="60" cy="60" r="42" className="vania-command-donut-track" />
        {visible.map((item, index) => {
          const percentage = sum > 0 ? (item.count / sum) * 100 : 0;
          const length = sum > 0 ? (item.count / sum) * 264 : 0;
          const label = getStatusLabel(item.status);
          const tone = statusTone(item.status);
          const circle = <circle key={item.status} role="button" tabIndex={0} aria-label={`${label}: ${formatPersianNumber(item.count)}، ${percentage.toFixed(1)}٪`} cx="60" cy="60" r="42" className={`vania-command-donut-segment ${tone}`} style={{ strokeDasharray: `${length} ${264 - length}`, strokeDashoffset: -offset }} onMouseEnter={() => setActive(index)} onMouseLeave={() => setActive(null)} onFocus={() => setActive(index)} onBlur={() => setActive(null)} onKeyDown={(event) => handleKeyboardActivation(event, () => setActive(index))} />;
          offset += length;
          return circle;
        })}
      </svg>
      <strong>{formatPersianNumber(sum)}</strong><small>کل سفارش</small>
      {active !== null && visible[active] ? <div className="vania-command-donut-tooltip">{getStatusLabel(visible[active].status)}: {formatPersianNumber(visible[active].count)} ({((visible[active].count / Math.max(1, sum)) * 100).toFixed(1)}٪)</div> : null}
    </div>
    <div className="vania-command-legend">{visible.map((item, index) => <span key={item.status} role="button" tabIndex={0} aria-label={`${getStatusLabel(item.status)}: ${formatPersianNumber(item.count)}، ${((item.count / Math.max(1, sum)) * 100).toFixed(1)}٪`} onMouseEnter={() => setActive(index)} onMouseLeave={() => setActive(null)} onFocus={() => setActive(index)} onBlur={() => setActive(null)} onKeyDown={(event) => handleKeyboardActivation(event, () => setActive(index))}><i className={`vania-command-status-dot ${statusTone(item.status)}`} /><span className="vania-command-status-label">{getStatusLabel(item.status)}</span><b>{formatPersianNumber(item.count)} ({((item.count / Math.max(1, sum)) * 100).toFixed(1)}٪)</b></span>)}</div>
  </div>;
}
